import { useEffect, useMemo, useState } from "react";
import { apiStaff } from "@/lib/api";
import { TIME_BLOCKS } from "@/components/SchedulePicker";
import { useRealtime } from "@/lib/realtime";
import { PageHeader, Card, Loading, StatusPill } from "@/components/admin";
import { ChevronLeft, ChevronRight, Plus, CalendarDays, Columns3 } from "lucide-react";

const WEEKDAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function dayKey(d) {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}
function startOfWeek(d) {
  const s = new Date(d);
  s.setHours(0, 0, 0, 0);
  s.setDate(s.getDate() - s.getDay());
  return s;
}
function fmtDate(d) {
  return `${WEEKDAY[d.getDay()]} ${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

/**
 * Pickup/drop-off availability calendar for dispatchers.
 * Day view: columns = technicians. Week view: columns = 7 days for one tech.
 * Booked slots show the job; empty slots are clickable "Open" -> book new job.
 */
export default function DispatchCalendar({ onJobClick, onNewJob }) {
  const [data, setData] = useState(null);
  const [view, setView] = useState("day"); // day | week
  const [anchor, setAnchor] = useState(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; });
  const [techId, setTechId] = useState(null);

  const reload = () => apiStaff.get("/crm/jobs/dispatch").then((r) => setData(r.data));
  useEffect(() => { reload(); }, []);
  useRealtime("staff", ["job."], reload);

  const techs = data?.technicians || [];
  const board = data?.board || {};

  // default selected tech for week view
  useEffect(() => {
    if (!techId && techs.length) setTechId(techs[0].id);
  }, [techs, techId]);

  // flatten jobs with a tech assignment key
  const jobsByTech = useMemo(() => {
    const map = {};
    for (const [tid, jobs] of Object.entries(board)) {
      map[tid] = (jobs || []).filter((j) => j.scheduled_for);
    }
    return map;
  }, [board]);

  function slotJobs(tid, date, startHour) {
    const dk = dayKey(date);
    return (jobsByTech[tid] || []).filter((j) => {
      const d = new Date(j.scheduled_for);
      const h = d.getHours();
      return dayKey(d) === dk && h >= startHour && h < startHour + 2;
    });
  }

  function bookSlot(tid, date, startHour) {
    const d = new Date(date);
    d.setHours(startHour, 0, 0, 0);
    onNewJob?.({
      scheduled_for: d.toISOString(),
      technician_id: tid === "unassigned" ? "" : tid,
    });
  }

  function shift(delta) {
    const d = new Date(anchor);
    d.setDate(d.getDate() + delta * (view === "week" ? 7 : 1));
    setAnchor(d);
  }
  function today() { const d = new Date(); d.setHours(0, 0, 0, 0); setAnchor(d); }

  const weekDays = useMemo(() => {
    const s = startOfWeek(anchor);
    return Array.from({ length: 7 }, (_, i) => { const d = new Date(s); d.setDate(d.getDate() + i); return d; });
  }, [anchor]);

  const cols = useMemo(() => {
    const list = [...techs.map((t) => ({ id: t.id, name: t.name, on_duty: t.on_duty }))];
    list.push({ id: "unassigned", name: "Unassigned", on_duty: null });
    return list;
  }, [techs]);

  if (!data) return <Loading />;

  const subtitle = view === "day"
    ? `${fmtDate(anchor)} · empty slots are open to book`
    : `Week of ${fmtDate(weekDays[0])} · per-technician availability`;

  return (
    <>
      <PageHeader
        title="Pickup / drop-off calendar"
        subtitle={subtitle}
        actions={
          <div className="flex items-center gap-2">
            <div className="inline-flex bg-[#FFFFFF] border border-[#002060]/15 rounded-md p-0.5 text-xs">
              <button
                data-testid="cal-view-day"
                onClick={() => setView("day")}
                className={`px-3 py-1.5 rounded flex items-center gap-1.5 transition-colors ${view === "day" ? "bg-[#FF6600] text-white" : "text-[#002060]/80"}`}
              >
                <CalendarDays className="w-3.5 h-3.5" /> Day
              </button>
              <button
                data-testid="cal-view-week"
                onClick={() => setView("week")}
                className={`px-3 py-1.5 rounded flex items-center gap-1.5 transition-colors ${view === "week" ? "bg-[#FF6600] text-white" : "text-[#002060]/80"}`}
              >
                <Columns3 className="w-3.5 h-3.5" /> Week
              </button>
            </div>
          </div>
        }
      />

      {/* Date navigator */}
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="inline-flex items-center gap-1">
          <button data-testid="cal-prev" onClick={() => shift(-1)} className="w-9 h-9 grid place-items-center rounded-md border border-[#002060]/15 text-[#002060] hover:border-[#FF6600] hover:text-[#FF6600] transition-colors">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button data-testid="cal-today" onClick={today} className="h-9 px-3 rounded-md border border-[#002060]/15 text-[#002060] text-sm font-semibold hover:border-[#FF6600] hover:text-[#FF6600] transition-colors">
            Today
          </button>
          <button data-testid="cal-next" onClick={() => shift(1)} className="w-9 h-9 grid place-items-center rounded-md border border-[#002060]/15 text-[#002060] hover:border-[#FF6600] hover:text-[#FF6600] transition-colors">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {view === "week" && (
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
            {cols.filter((c) => c.id !== "unassigned").map((t) => (
              <button
                key={t.id}
                data-testid={`cal-tech-${t.id}`}
                onClick={() => setTechId(t.id)}
                className={`shrink-0 h-9 px-3 rounded-full text-sm font-semibold border transition-colors ${techId === t.id ? "bg-[#FF6600] text-white border-[#FF6600]" : "bg-[#FFFFFF] text-[#002060] border-[#002060]/15 hover:border-[#FF6600]"}`}
              >
                {t.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {view === "day" ? (
        <DayGrid cols={cols} date={anchor} slotJobs={slotJobs} onJobClick={onJobClick} onBook={bookSlot} />
      ) : (
        <WeekGrid days={weekDays} techId={techId} slotJobs={slotJobs} onJobClick={onJobClick} onBook={bookSlot} />
      )}
    </>
  );
}

function SlotCell({ jobs, onJobClick, onBook, testid }) {
  if (jobs.length === 0) {
    return (
      <button
        data-testid={`${testid}-open`}
        onClick={onBook}
        className="w-full min-h-[52px] rounded-md border border-dashed border-emerald-300 bg-emerald-50/50 text-emerald-700 text-xs font-semibold hover:bg-emerald-100 hover:border-emerald-400 transition-colors flex items-center justify-center gap-1"
      >
        <Plus className="w-3 h-3" /> Open
      </button>
    );
  }
  return (
    <div className="space-y-1.5">
      {jobs.map((j) => (
        <button
          key={j.id}
          data-testid={`${testid}-job-${j.id}`}
          onClick={() => onJobClick?.(j.id)}
          className="w-full text-left rounded-md border border-[#002060]/15 bg-[#FFFFFF] px-2 py-1.5 hover:border-[#FF6600] hover:shadow-sm transition-all"
        >
          <div className="text-[12px] font-semibold text-[#002060] capitalize truncate">{j.appliance}</div>
          <div className="text-[11px] text-[#002060]/70 truncate">{j.customer_name}</div>
          <div className="text-[10px] text-[#002060]/50 truncate">{j.address}</div>
          <div className="mt-1"><StatusPill status={j.status} /></div>
        </button>
      ))}
    </div>
  );
}

function DayGrid({ cols, date, slotJobs, onJobClick, onBook }) {
  return (
    <Card padded={false}>
      <div className="overflow-x-auto">
        <div className="min-w-max">
          {/* header */}
          <div className="flex border-b border-[#002060]/10 bg-[#002060]/[0.02]">
            <div className="w-28 shrink-0 px-3 py-2.5 text-[11px] uppercase tracking-widest font-bold text-[#002060]/60">Time</div>
            {cols.map((c) => (
              <div key={c.id} className="w-48 shrink-0 px-3 py-2.5 border-l border-[#002060]/10">
                <div className="text-sm font-semibold text-[#002060] truncate">{c.name}</div>
                {c.on_duty !== null && (
                  <StatusPill tone={c.on_duty ? "emerald" : "slate"} label={c.on_duty ? "on duty" : "off"} />
                )}
              </div>
            ))}
          </div>
          {/* rows */}
          {TIME_BLOCKS.map((b) => (
            <div key={b.startHour} className="flex border-b border-[#002060]/10 last:border-0">
              <div className="w-28 shrink-0 px-3 py-2.5 text-xs font-semibold text-[#002060]/80 bg-[#002060]/[0.02]">{b.label}</div>
              {cols.map((c) => (
                <div key={c.id} className="w-48 shrink-0 p-2 border-l border-[#002060]/10">
                  <SlotCell
                    jobs={slotJobs(c.id, date, b.startHour)}
                    onJobClick={onJobClick}
                    onBook={() => onBook(c.id, date, b.startHour)}
                    testid={`cal-day-${c.id}-${b.startHour}`}
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

function WeekGrid({ days, techId, slotJobs, onJobClick, onBook }) {
  const today = dayKey(new Date());
  return (
    <Card padded={false}>
      <div className="overflow-x-auto">
        <div className="min-w-max">
          {/* header */}
          <div className="flex border-b border-[#002060]/10 bg-[#002060]/[0.02]">
            <div className="w-28 shrink-0 px-3 py-2.5 text-[11px] uppercase tracking-widest font-bold text-[#002060]/60">Time</div>
            {days.map((d) => (
              <div key={dayKey(d)} className={`w-40 shrink-0 px-3 py-2.5 border-l border-[#002060]/10 ${dayKey(d) === today ? "bg-[#FF6600]/10" : ""}`}>
                <div className="text-[10px] uppercase tracking-widest font-semibold text-[#002060]/60">{WEEKDAY[d.getDay()]}</div>
                <div className="text-sm font-bold text-[#002060] num">{MONTHS[d.getMonth()]} {d.getDate()}</div>
              </div>
            ))}
          </div>
          {/* rows */}
          {TIME_BLOCKS.map((b) => (
            <div key={b.startHour} className="flex border-b border-[#002060]/10 last:border-0">
              <div className="w-28 shrink-0 px-3 py-2.5 text-xs font-semibold text-[#002060]/80 bg-[#002060]/[0.02]">{b.label}</div>
              {days.map((d) => (
                <div key={dayKey(d)} className="w-40 shrink-0 p-2 border-l border-[#002060]/10">
                  <SlotCell
                    jobs={techId ? slotJobs(techId, d, b.startHour) : []}
                    onJobClick={onJobClick}
                    onBook={() => onBook(techId, d, b.startHour)}
                    testid={`cal-week-${dayKey(d)}-${b.startHour}`}
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}
